/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                                  ADMIN HUB DASHBOARD                                         ║
 * ║                          Central Navigation for Administrative Functions                      ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Shield, 
  Users, 
  FileCheck, 
  Activity, 
  Database, 
  Calendar,
  BarChart3,
  Settings,
  ArrowLeft,
  Building2,
  Upload,
  Download
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

type ScreenType = 'dashboard' | 'admin' | 'bulk-approval' | 'scraper-control' | 'scraper-monitoring' | 'import-schedule';

interface AdminHubProps {
  onNavigate: (screen: ScreenType) => void;
}

interface AdminCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  screen: ScreenType;
  requiresSuperAdmin?: boolean;
  badge?: string;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red';
}

export function AdminHub({ onNavigate }: AdminHubProps) {
  const { hasAdminAccess, hasSuperAdminAccess, user } = useAuth();

  // Admin functionality cards
  const adminCards: AdminCard[] = [
    {
      id: 'bulk-approval',
      title: 'Bulk Company Approval',
      description: 'Review and approve company registrations in bulk operations',
      icon: <Building2 className="w-6 h-6" />,
      screen: 'bulk-approval',
      badge: 'Essential',
      color: 'green'
    },
    {
      id: 'scraper-control',
      title: 'Scraper Control Panel',
      description: 'Manage external data scraper operations and configurations',
      icon: <Database className="w-6 h-6" />,
      screen: 'scraper-control',
      requiresSuperAdmin: true,
      badge: 'Advanced',
      color: 'blue'
    },
    {
      id: 'scraper-monitoring',
      title: 'Scraper Monitoring',
      description: 'Monitor scraper performance and view operational metrics',
      icon: <Activity className="w-6 h-6" />,
      screen: 'scraper-monitoring',
      requiresSuperAdmin: true,
      badge: 'Monitor',
      color: 'purple'
    },
    {
      id: 'import-schedule',
      title: 'Import Schedule Manager',
      description: 'Manage automated import schedules and data pipelines',
      icon: <Calendar className="w-6 h-6" />,
      screen: 'import-schedule',
      requiresSuperAdmin: true,
      badge: 'System',
      color: 'orange'
    }
  ];

  // Filter cards based on user permissions
  const visibleCards = adminCards.filter(card => {
    if (card.requiresSuperAdmin) {
      return hasSuperAdminAccess;
    }
    return hasAdminAccess;
  });

  const getCardColorClasses = (color: string) => {
    switch (color) {
      case 'green': return 'border-green-200 hover:border-green-300 hover:shadow-green-100';
      case 'blue': return 'border-blue-200 hover:border-blue-300 hover:shadow-blue-100';
      case 'purple': return 'border-purple-200 hover:border-purple-300 hover:shadow-purple-100';
      case 'orange': return 'border-orange-200 hover:border-orange-300 hover:shadow-orange-100';
      case 'red': return 'border-red-200 hover:border-red-300 hover:shadow-red-100';
      default: return 'border-gray-200 hover:border-gray-300 hover:shadow-gray-100';
    }
  };

  const getBadgeColorClasses = (color: string) => {
    switch (color) {
      case 'green': return 'bg-green-100 text-green-800';
      case 'blue': return 'bg-blue-100 text-blue-800';
      case 'purple': return 'bg-purple-100 text-purple-800';
      case 'orange': return 'bg-orange-100 text-orange-800';
      case 'red': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigate('dashboard')}
                className="hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <Shield className="w-8 h-8 text-blue-600" />
                  Admin Control Center
                </h1>
                <p className="text-gray-600 mt-1">
                  Administrative tools and system management
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasSuperAdminAccess ? (
                <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                  <Shield className="w-3 h-3 mr-1" />
                  Super Admin
                </Badge>
              ) : hasAdminAccess ? (
                <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                  <Users className="w-3 h-3 mr-1" />
                  Admin
                </Badge>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Admin Cards Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {visibleCards.map((card) => (
            <Card
              key={card.id}
              className={`transition-all duration-200 hover:shadow-lg cursor-pointer ${getCardColorClasses(card.color)}`}
              onClick={() => onNavigate(card.screen)}
            >
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-${card.color}-50`}>
                      {card.icon}
                    </div>
                    <span className="text-xl">{card.title}</span>
                  </div>
                  {card.badge && (
                    <Badge
                      variant="secondary"
                      className={getBadgeColorClasses(card.color)}
                    >
                      {card.badge}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {card.description}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center text-xs text-gray-500">
                    {card.requiresSuperAdmin ? (
                      <span className="flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        Super Admin Only
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Admin Access
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs hover:bg-gray-100"
                  >
                    Open →
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Access Info */}
        <div className="mt-8">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">Access Level Information</h3>
                  <p className="text-blue-700 text-sm">
                    {user?.email} • 
                    {hasSuperAdminAccess 
                      ? ' Full system administration access' 
                      : hasAdminAccess 
                        ? ' Standard administrative access'
                        : ' Limited access - contact super admin for permissions'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onNavigate('dashboard')}
              className="hover:bg-gray-50"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              View Dashboard
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled
              className="opacity-50"
            >
              <Settings className="w-4 h-4 mr-2" />
              System Settings
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled
              className="opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Data
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}